import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  Button,
  Collapse,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const ChatBot = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);
  const messagesEndRef = useRef(null);
  const theme = useTheme();
  const { isAuthenticated } = useAuth();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Welcome message
      setMessages([
        {
          id: 1,
          type: 'bot',
          content: `Hello! I'm your Alzheimer's Care Assistant. I'm here to help answer questions about Alzheimer's disease, symptoms, diagnosis, treatment options, and caregiving.

What would you like to know about Alzheimer's disease today?`,
          timestamp: new Date(),
        },
      ]);
    }
  }, [isOpen, messages.length]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    
    if (!isAuthenticated) {
      setError('Please sign up to use the chat feature.');
      return;
    }

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError('');

    try {
      const response = await api.post('/chat', {
        message: inputMessage,
        conversationHistory: messages.slice(-5), // Send last 5 messages for context
      });

      const data = response.data;

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: data.response,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setError('');
    // Re-add welcome message
    setMessages([
      {
        id: 1,
        type: 'bot',
        content: `Hello! I'm your Alzheimer's Care Assistant. I'm here to help answer questions about Alzheimer's disease, symptoms, diagnosis, treatment options, and caregiving.

What would you like to know about Alzheimer's disease today?`,
        timestamp: new Date(),
      },
    ]);
  };

  const formatTimestamp = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const quickQuestions = [
    "What are the early signs of Alzheimer's?",
    "How is Alzheimer's diagnosed?",
    "What treatments are available?",
    "How can I care for someone with Alzheimer's?",
    "What causes Alzheimer's disease?",
    "Is there a cure for Alzheimer's?",
  ];

  const handleQuickQuestion = (question) => {
    setInputMessage(question);
  };

  if (!isOpen) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        width: { xs: '90vw', sm: '400px' },
        maxWidth: '400px',
        height: isExpanded ? '600px' : '60px',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1300,
        borderRadius: 3,
        overflow: 'hidden',
        border: `2px solid ${theme.palette.primary.main}`,
        transition: 'height 0.3s ease-in-out',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ bgcolor: 'white', color: theme.palette.primary.main, mr: 1 }}>
            <BotIcon />
          </Avatar>
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                fontSize: '1rem',
                color: 'rgba(255, 255, 255, 1)',
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
              }}
            >
              AI Healthcare Assistant
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(255, 255, 255, 1)',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                fontWeight: 500
              }}
            >
              AI healthcare support powered by Google Gemini 2.5 Flash
            </Typography>
          </Box>
        </Box>
        <Box>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            sx={{ color: 'white', mr: 1 }}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 1,
            backgroundColor: '#f8f9fa',
            maxHeight: '400px',
          }}
        >
          <Alert severity="info" sx={{ mb: 2, fontSize: '0.75rem' }}>
            <AlertTitle sx={{ fontSize: '0.875rem', mb: 0.5 }}>Important Notice</AlertTitle>
            <Typography variant="caption" component="div">
              This information is for educational purposes only and should not replace professional medical advice. 
              Always consult with healthcare providers for personalized diagnosis and treatment.
            </Typography>
          </Alert>
          
          {!isAuthenticated && (
            <Alert severity="warning" sx={{ mb: 2, fontSize: '0.875rem' }}>
              Please sign up to use the chat feature.
            </Alert>
          )}

          {messages.length === 0 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Start a conversation about Alzheimer's disease
              </Typography>
            </Box>
          )}

          <List sx={{ p: 0 }}>
            {messages.map((message) => (
              <ListItem
                key={message.id}
                sx={{
                  display: 'flex',
                  flexDirection: message.type === 'user' ? 'row-reverse' : 'row',
                  alignItems: 'flex-start',
                  mb: 1,
                }}
              >
                <ListItemAvatar
                  sx={{
                    minWidth: 40,
                    ...(message.type === 'user' && { ml: 1, mr: 0 }),
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: message.type === 'user' ? theme.palette.secondary.main : theme.palette.primary.main,
                      width: 32,
                      height: 32,
                    }}
                  >
                    {message.type === 'user' ? <PersonIcon /> : <BotIcon />}
                  </Avatar>
                </ListItemAvatar>
                <Box
                  sx={{
                    maxWidth: '75%',
                    ...(message.type === 'user' && { textAlign: 'right' }),
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1.5,
                      bgcolor: message.type === 'user' ? theme.palette.primary.light : 'white',
                      color: message.type === 'user' ? 'white' : '#212121',
                      borderRadius: 2,
                      ...(message.type === 'user' && {
                        borderBottomRightRadius: 4,
                      }),
                      ...(message.type === 'bot' && {
                        borderBottomLeftRadius: 4,
                      }),
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        whiteSpace: 'pre-line',
                        color: message.type === 'user' ? 'white' : '#212121',
                        fontWeight: message.type === 'bot' ? 400 : 'normal'
                      }}
                    >
                      {message.content}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        fontSize: '0.75rem',
                        color: message.type === 'user' 
                          ? 'rgba(255, 255, 255, 0.85)' 
                          : '#616161',
                        fontWeight: 400
                      }}
                    >
                      {formatTimestamp(message.timestamp)}
                    </Typography>
                  </Paper>
                </Box>
              </ListItem>
            ))}
          </List>

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Thinking...
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ m: 1 }}>
              {error}
              <Button size="small" onClick={() => setError('')} sx={{ ml: 1 }}>
                Dismiss
              </Button>
            </Alert>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Quick Questions */}
        {messages.length <= 1 && (
          <Box sx={{ p: 1, backgroundColor: 'background.paper' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Quick questions:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {quickQuestions.slice(0, 3).map((question, index) => (
                <Chip
                  key={index}
                  label={question}
                  size="small"
                  clickable
                  onClick={() => handleQuickQuestion(question)}
                  sx={{ fontSize: '0.7rem', height: 24 }}
                />
              ))}
            </Box>
          </Box>
        )}

        <Divider />

        {/* Input Area */}
        <Box sx={{ p: 1, backgroundColor: 'background.paper' }}>
          {!isAuthenticated && (
            <Alert severity="warning" sx={{ mb: 1, fontSize: '0.875rem' }}>
              Please sign up to use the chat feature.
            </Alert>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder={isAuthenticated ? "Ask about Alzheimer's symptoms, treatments, care..." : "Sign up to use chat..."}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              size="small"
              disabled={!isAuthenticated}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <IconButton
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading || !isAuthenticated}
              color="primary"
              size="small"
            >
              <SendIcon />
            </IconButton>
            <IconButton
              onClick={clearChat}
              size="small"
              color="secondary"
              title="Clear chat"
              disabled={!isAuthenticated}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ChatBot; 